/**
 * Module availability — the ONE rule for "may this workspace use module X?"
 * (ADR-0100).
 *
 * A module is available to a workspace only when (1) the deployment installed
 * it, (2) the tenant's administrator has it switched on, and (3) every module
 * it needs is available too. Auth applies this rule once and every consumer —
 * the session, the gateway, playbook-be, notification and Player AI — reads
 * Auth's answer, so they cannot disagree.
 *
 * Three key kinds take part, and they behave differently:
 *
 * - INSTALLABLE keys are what a deployment installs (Helm `modules.installed`).
 *   They are product ids plus `knowledge_studio`, which is an app with no
 *   playbook-be module. `ProductModuleId` stays playbook-be's own list.
 * - CAPABILITY keys (`email`, `rbta`) are never "installed" — every deployment
 *   carries them — but the administrator's switch still applies.
 * - Anything else in a tenant's enabled set is not a module this platform
 *   knows, and is dropped rather than passed through.
 *
 * The same silent-never-match warning as `module-ids.ts` applies: a wrong key
 * does not throw, it compares equal to nothing. So the product → Auth
 * translation for installable keys lives here, once.
 */
import { AUTH_MODULE_KEY_BY_PRODUCT_MODULE_ID, EMAIL_MODULE_KEY, } from "./module-ids.js";
/** Every installable key, in the product vocabulary. */
export const INSTALLABLE_MODULE_KEYS = [
    "solve",
    "vsm",
    "hoshin",
    "kpi",
    "lama",
    "knowledge_studio",
];
/**
 * Installable key → Auth catalogue key. Exhaustive by type, so a new
 * installable key without its Auth spelling fails to compile.
 */
export const AUTH_KEY_BY_INSTALLABLE_MODULE = {
    ...AUTH_MODULE_KEY_BY_PRODUCT_MODULE_ID,
    knowledge_studio: "knowledge_studio",
};
/**
 * Catalogue keys that are platform capabilities, not installable apps. They
 * skip the "installed" check but keep the administrator's switch: an email
 * switch-off stays off whatever the installed list says.
 */
export const CAPABILITY_MODULE_KEYS = [EMAIL_MODULE_KEY, "rbta"];
/**
 * Hard requirements, in the installable vocabulary (d1): Hoshin's targets are
 * KPI rows, so Hoshin is unusable without KPI Manager. KPI Manager does not
 * need Hoshin. Every other pair is soft — a link option hides, nothing breaks.
 */
export const MODULE_REQUIRES = {
    hoshin: ["kpi"],
};
export function isInstallableModuleKey(value) {
    return Object.hasOwn(AUTH_KEY_BY_INSTALLABLE_MODULE, value);
}
/** Translate an installable key into its Auth catalogue key; `null` if unknown. */
export function authKeyForInstallableModule(key) {
    return isInstallableModuleKey(key)
        ? AUTH_KEY_BY_INSTALLABLE_MODULE[key]
        : null;
}
/**
 * The problems with a deployment's installed list: unknown keys, duplicates,
 * and any module whose requirement is missing. An empty array means valid.
 * Auth, playbook-be and the Helm chart refuse a list with problems.
 */
export function installedModulesProblems(installed) {
    const problems = [];
    const seen = new Set();
    for (const key of installed) {
        if (!isInstallableModuleKey(key)) {
            problems.push(`unknown installable module '${key}'`);
        }
        else if (seen.has(key)) {
            problems.push(`installable module '${key}' is listed twice`);
        }
        seen.add(key);
    }
    for (const key of seen) {
        if (!isInstallableModuleKey(key))
            continue;
        for (const required of MODULE_REQUIRES[key] ?? []) {
            if (!seen.has(required)) {
                problems.push(`'${key}' requires '${required}', which is not installed`);
            }
        }
    }
    return problems;
}
/**
 * Parse a comma-separated installed list (`DEPLOYED_MODULES`). Unset or blank
 * answers `null`, which means "everything installed" — today's behaviour.
 * Throws on any problem so a service refuses to start rather than guess.
 */
export function parseInstalledModules(raw) {
    if (raw === undefined || raw === null || raw.trim() === "")
        return null;
    const keys = raw
        .split(",")
        .map((key) => key.trim())
        .filter((key) => key.length > 0);
    const problems = installedModulesProblems(keys);
    if (problems.length > 0) {
        throw new Error(`invalid installed module list: ${problems.join("; ")}`);
    }
    return keys;
}
const CAPABILITY_KEY_SET = new Set(CAPABILITY_MODULE_KEYS);
/**
 * The available Auth keys for one tenant:
 * `tenant-on ∩ (installed keys, translated ∪ capability keys)`, then minus
 * every module whose requirement is not itself available. Order follows the
 * tenant's stored order, so callers that compared lists before still can.
 */
export function availableModuleKeys(input) {
    const installedAuthKeys = new Set((input.installed ?? INSTALLABLE_MODULE_KEYS).map((key) => AUTH_KEY_BY_INSTALLABLE_MODULE[key]));
    const candidates = [];
    const seen = new Set();
    for (const key of input.tenantEnabledKeys) {
        if (seen.has(key))
            continue;
        seen.add(key);
        if (installedAuthKeys.has(key) || CAPABILITY_KEY_SET.has(key)) {
            candidates.push(key);
        }
    }
    // Requirements can chain, so drop until nothing more falls out.
    let available = new Set(candidates);
    for (;;) {
        const next = new Set([...available].filter((authKey) => requirementsMet(authKey, available)));
        if (next.size === available.size)
            break;
        available = next;
    }
    return candidates.filter((key) => available.has(key));
}
function requirementsMet(authKey, available) {
    const installable = INSTALLABLE_MODULE_KEYS.find((key) => AUTH_KEY_BY_INSTALLABLE_MODULE[key] === authKey);
    if (installable === undefined)
        return true;
    return (MODULE_REQUIRES[installable] ?? []).every((required) => available.has(AUTH_KEY_BY_INSTALLABLE_MODULE[required]));
}
/**
 * Per-module status for every installable key. `availableAuthKeys` is the
 * output of `availableModuleKeys` (or Auth's answer, which is the same thing);
 * `installed` decides `not_installed`.
 */
export function moduleAvailabilityStatus(input) {
    const available = new Set(input.availableAuthKeys);
    const installed = new Set(input.installed ?? INSTALLABLE_MODULE_KEYS);
    const status = {};
    for (const key of INSTALLABLE_MODULE_KEYS) {
        status[key] = !installed.has(key)
            ? "not_installed"
            : available.has(AUTH_KEY_BY_INSTALLABLE_MODULE[key])
                ? "available"
                : "turned_off";
    }
    return status;
}
/**
 * The one wording pair for a module a person cannot use (d7): an administrator
 * switched it off, or this deployment does not include it. Screens and Player
 * AI's server text say the same thing; `available` has no message.
 */
export function moduleUnavailablePhrase(label, status) {
    return status === "turned_off"
        ? `${label} is turned off for this workspace`
        : `${label} isn't available in this workspace`;
}
/** `MODULE_REQUIRES` in Auth keys: an Auth key → the Auth keys it needs. */
const REQUIRED_AUTH_KEYS = new Map(INSTALLABLE_MODULE_KEYS.map((key) => [
    AUTH_KEY_BY_INSTALLABLE_MODULE[key],
    (MODULE_REQUIRES[key] ?? []).map((required) => AUTH_KEY_BY_INSTALLABLE_MODULE[required]),
]));
/**
 * The other tenant switches a module change must move too, so the stored
 * switches never break a requirement (d2). In Auth keys, because the switches
 * are stored that way. Turning a module ON needs every module it requires on;
 * turning one OFF needs every module that requires it off. Chains are
 * followed, and only modules not already in the target state are named, so an
 * empty answer means the change breaks no rule — turning KPI Manager on, or
 * Hoshin off, never touches the other. Auth enforces it (409 without
 * `includeDependencies`); the admin screen uses it to word the confirm dialog.
 */
export function moduleSwitchChanges(moduleKey, status, storedEnabledKeys) {
    const enabled = new Set(storedEnabledKeys);
    const turningOn = status === "enabled";
    const linked = (key) => turningOn
        ? (REQUIRED_AUTH_KEYS.get(key) ?? [])
        : [...REQUIRED_AUTH_KEYS]
            .filter(([, required]) => required.includes(key))
            .map(([dependent]) => dependent);
    const changes = [];
    const visited = new Set([moduleKey]);
    const visit = (key) => {
        for (const other of linked(key)) {
            if (visited.has(other))
                continue;
            visited.add(other);
            if (enabled.has(other) !== turningOn)
                changes.push(other);
            visit(other);
        }
    };
    visit(moduleKey);
    return changes;
}
/**
 * One module's answer for a client that holds the installed list and Auth's
 * available set (the session claim): installed AND available. Unknown keys are
 * never available.
 */
export function isInstalledModuleAvailable(key, input) {
    const authKey = authKeyForInstallableModule(key);
    if (authKey === null || !input.installed.includes(key))
        return false;
    return new Set(input.availableAuthKeys).has(authKey);
}
/**
 * Link kind → the module that owns it. `external_ref` belongs to no module
 * (Jira and similar live outside the platform). Exhaustive by type, so a new
 * link kind fails to compile until its owner is declared here.
 */
export const WORK_LINK_REF_MODULE = {
    hoshin_objective: "hoshin",
    hoshin_priority: "hoshin",
    hoshin_target: "hoshin",
    hoshin_gap: "hoshin",
    kpi: "kpi",
    vsm_kaizen: "vsm",
    solve: "solve",
    vsm: "vsm",
    external_ref: null,
};
/** Whether a string is a link kind this platform defines at all. */
export function isWorkLinkRefName(value) {
    return Object.hasOwn(WORK_LINK_REF_MODULE, value);
}
/**
 * The module that owns a link kind, or `null` for `external_ref` and for any
 * unknown kind — use `isWorkLinkRefName` first when the difference matters.
 */
export function moduleForWorkLinkRef(refName) {
    return isWorkLinkRefName(refName) ? WORK_LINK_REF_MODULE[refName] : null;
}
//# sourceMappingURL=module-availability.js.map